import constants, { publicConstants } from '../../constants';
import {
  processViewResponse,
  SCOPE_TYPES,
  processHeaders,
  shouldReplaceActiveItem
} from '../../store/state/epics/util';
import * as actionType from '../../store/state/actions';
import * as actions from '../../store/state/actions/action-creators';
import { isAssignmentInCreateStage, isAssignmentInManualStage } from '../../utils/case-utils';
import {
  getCaseInfo,
  getCaseInfoID,
  getContainerName,
  getDisplayCaseID,
  isWorkAreaContainer,
  addActionToRemoveContainerItem,
  canLaunchCaseInModal
} from '../common-helper';
import {
  getDataContextName,
  getActiveContainerItemContext,
  getTargetFromContainerItemID,
  getContainerItemName,
  isContainerSingleMode,
  hasContainerItems
} from '../../container/container-utils';
import { getSemanticURL } from '../../router/app-router';
import EnvironmentInfo from '../../environment-info';
import type { Action } from '../../store/state/state-manager';
import type { DxAPIResponseObject } from '../types';
import type { Actionout } from '../../types';
import RemoteCase from '../remote-case';
import { ActionManagerInstance } from '../../actions/manager';
import type { ExtendedActionObject } from '../../globals';

const { RESOURCE_TYPES } = publicConstants;

export const getViewType = (actionInPayload: Action['payload'], dxApiResponse: any = []) => {
  const { viewType, containerName, targetContainerName, isChild } = actionInPayload;
  const caseInfo = getCaseInfo(dxApiResponse);
  if (isAssignmentInManualStage(caseInfo) && actionInPayload?.stageID === caseInfo.stageID) {
    return SCOPE_TYPES.PAGE;
  }

  if (!viewType) {
    return (isWorkAreaContainer(containerName) || isWorkAreaContainer(targetContainerName)) && !isChild
      ? SCOPE_TYPES.FORM
      : SCOPE_TYPES.PAGE;
  }

  return viewType;
};

export const getContainerNameForCaseStage = (
  dxApiResponse: DxAPIResponseObject,
  actionInPayload: Action['payload']
) => {
  const caseInfo = getCaseInfo(dxApiResponse);
  let isInCreateStage;
  if (EnvironmentInfo.getDefaultCasePage()) {
    isInCreateStage = false;
  } else {
    isInCreateStage = isAssignmentInCreateStage(caseInfo);
  }
  const { isChild, containerName, isTraditionalUICase } = actionInPayload;
  /*
    Scenario: Case opened using openWorkByHandle opens case details page with Case Summary, ToDo gadget and other gadgets rendered.
    Opening assignment from here by clicking on Go should render assignment inline even in case of create stage if the case is not a child case.
    If the case is child case then the case should be opened in modal. So having a check if the openAssignment action is from ToDo list gadget.
  */
  const isChildCaseInCreateStage = isChild && isInCreateStage;
  const isCreateStageOpenAssignmentFromWorklistToDoList = isInCreateStage && !actionInPayload.isActionFromToDoList;
  const isCreateStageActive = isChildCaseInCreateStage || isCreateStageOpenAssignmentFromWorklistToDoList;
  return isCreateStageActive && !isTraditionalUICase
    ? constants.MODAL
    : containerName || getContainerName(actionType.ACTION_OPENASSIGNMENT, actionInPayload);
};

export const addActionToActivateContainerItem = async (
  actionsOut: Actionout[],
  containerItemData: { [key: string]: any },
  options: { [key: string]: any }
) => {
  const { semanticURL, containerItemID, isParallelAssignment, insKey } = containerItemData;
  const { target, actionInPayload } = options;
  const { skipBrowserSemanticUrlUpdate = false } = actionInPayload;
  const context = getDataContextName(containerItemID);
  /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
  await processViewResponse(
    {
      directUrlUpdation: !skipBrowserSemanticUrlUpdate,
      semanticURL
    },
    actionInPayload,
    {
      acName: target,
      target,
      context
    }
  );

  actionsOut.push(
    actions.postProcess(actions.activateContainerItem, {
      containerItemID,
      target,
      isParallelAssignment,
      insKey,
      resourceType: RESOURCE_TYPES.CASE,
      context,
      doDeleteCaseSummary: actionInPayload.doDeleteCaseSummary
    })
  );
};

export const setDirectUrlUpdation = (dxApiResponse: DxAPIResponseObject, actionInPayload: Action['payload']) => {
  const caseInfo = getCaseInfo(dxApiResponse);
  const caseInfoID = getCaseInfoID(dxApiResponse);
  const isInCreateStage = isAssignmentInCreateStage(caseInfo);
  const { skipBrowserSemanticUrlUpdate = false, viewType, isTraditionalUICase } = actionInPayload;
  const canUpdateTraditionalCaseURL = isTraditionalUICase && !canLaunchCaseInModal();
  // URL and title updation should be done only for response content which is replacing entire case view.
  const updateUrl =
    viewType === SCOPE_TYPES.PAGE && (!isInCreateStage || canUpdateTraditionalCaseURL) && !skipBrowserSemanticUrlUpdate;

  const { caseTypeID } = caseInfo;
  if (updateUrl) {
    const semanticURL = getSemanticURL(actionType.ACTION_OPENWORKBYHANDLE, {
      caseClassName: caseTypeID
    }).replace(/{.+?}/, String(getDisplayCaseID(caseInfoID)));
    dxApiResponse.semanticURL = semanticURL;
    dxApiResponse.directUrlUpdation = true;
  }
};
export const addRemoteHeadersIfRequired = (actionInPayload: Action['payload']) => {
  let requestHeaders = actionInPayload?.headers ?? {};
  if (actionInPayload?.isRemote) {
    requestHeaders = processHeaders(requestHeaders, actionInPayload.remoteClassName);
  }
  return requestHeaders;
};

const processContainerItemInSingleMode = (
  actionInPayload: Action['payload'],
  response: any,
  payload: { [key: string]: any; caseViewMode?: any },
  actionInCaseViewMode: string,
  actionsOut: Action[]
) => {
  if (getViewType(actionInPayload, response) === SCOPE_TYPES.FORM) {
    // for viewType=form, usecases ==> caseViewMode is generally perform mode but consumer can override sending through payload.
    payload.caseViewMode = actionInCaseViewMode || constants.PERFORM_MODE;
    actionsOut.push(actions.postProcess(actions.updateContainerItem, payload));
  } else {
    actionsOut.push(actions.postProcess(actions.replaceContainerItem, payload));
  }
};

const processContainerItemsInMultipleMode = (
  actionsOut: Action[],
  removeActionPayload: Action['payload'],
  target: string | null,
  payload: any,
  actionInPayload: Action['payload']
) => {
  /*
    - container in multiple mode (e.g. primary, modal).
    - container in single mode with no items present in it.
  */
  addActionToRemoveContainerItem(actionsOut, {
    removeActionPayload,
    target
  });
  if (shouldReplaceActiveItem(payload.key, target)) {
    payload.context = getActiveContainerItemContext(target);
    payload.containerItemID = payload.context;
    actionsOut.push(actions.postProcess(actions.replaceContainerItem, payload));
  } else if ((actionInPayload?.isChild || actionInPayload?.isRemote) && actionInPayload?.isMultiDoc) {
    const containerTarget = getTargetFromContainerItemID(actionInPayload.context);
    payload.target = containerTarget;
    payload.context = actionInPayload.context;
    payload.containerItemID = actionInPayload.context;
    actionsOut.push(actions.postProcess(actions.replaceContainerItem, payload));
  } else {
    const containerTarget = getTargetFromContainerItemID(payload.context);
    const containerItemName = getContainerItemName(containerTarget, payload.key);
    if (containerItemName) {
      payload.target = containerTarget;
      payload.context = containerItemName;
      payload.containerItemID = containerItemName;
      actionsOut.push(actions.postProcess(actions.replaceContainerItem, payload));
    } else {
      actionsOut.push(actions.postProcess(actions.addContainerItem, payload));
    }
  }
};

export const processAppropriateContainerItem = (
  payload: Action['payload'],
  target: string | null,
  actionsOut: Action[],
  actionInPayload: Action['payload'],
  response: any,
  actionInCaseViewMode: string,
  removeActionPayload: Action['payload']
) => {
  if (isContainerSingleMode(target) && hasContainerItems(target)) {
    processContainerItemInSingleMode(actionInPayload, response, payload, actionInCaseViewMode, actionsOut);
  } else {
    processContainerItemsInMultipleMode(actionsOut, removeActionPayload, target, payload, actionInPayload);
  }
};

export const setIsFirstAssignmentForCoexistence = (actionInPayload: Action['payload'], payload: any): void => {
  if (actionInPayload.acTertiary) {
    payload.data.caseInfo.isFirstAssignment = actionInPayload.acTertiary;
  }
};

export const validateAndSetRemoteCaseParams = (className: string, actionConfig: ExtendedActionObject) => {
  className = RemoteCase.getRemoteCaseClassBySourceClass(className) ?? className;
  if (RemoteCase.isRemoteCase(className)) {
    const { RemoteClassName, RemoteSystemID, name } = RemoteCase.getRemoteCase(className);
    if (!actionConfig.headers?.RemoteSystemID) {
      ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, RemoteSystemID);
    }
    if (RemoteCase.isTraditionalRemoteCase(className)) {
      actionConfig.assignmentID = actionConfig.assignments?.[0]?.ID;
      const remoteCaseActionDetails: ExtendedActionObject = {
        className,
        name,
        action: actionType.OPENASSIGNMENT,
        remoteClassName: RemoteClassName
      };
      ActionManagerInstance.setTraditionalRemoteCaseParams(actionConfig, remoteCaseActionDetails);
    }
  }
};
